import { ActionIcon, Text } from "@mantine/core";
import { X } from "lucide-react";
import { useEffect, useId, useRef, type PointerEvent, type ReactNode } from "react";

import { sheetStop, type SheetHeight } from "./phone";
import classes from "./editor.module.css";

// The phone's top bar and bottom row, as `editor.module.css` sizes them (`--phone-top`, `--phone-row`).
const TOP_PX = 56;
const ROW_PX = 60;
// A press on the handle that moves less than this is a tap, whose click turns half height into full and back.
const TAP_PX = 6;

/**
 * A panel's frame, which is a bottom sheet on a phone (spec phone-layout.md): half the room above
 * the bottom row at first, all of it below the top bar after a drag up on its handle. Let go, the
 * drag settles at the nearest of closed, half and full (`sheetStop`); the close and Escape close
 * it, and the screen's dim does too. Closed, it hides its panel.
 *
 * It is always the same element, its panel always its first child, and the desktop draws no box
 * for it (`display: contents`). So the panel keeps its state when the sheet closes or the phone
 * turns sideways: the media panel's upload carries on, and the assistant keeps its draft.
 */
export function BottomSheet(props: {
  phone: boolean;
  open: boolean;
  title: string;
  height: SheetHeight;
  onHeight: (height: SheetHeight) => void;
  onClose: () => void;
  children: ReactNode;
}) {
  const { phone, open, title, height, onHeight, onClose } = props;
  const shown = phone && open;
  const sheet = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; pointer: number } | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!shown) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [shown, onClose]);

  const room = () => {
    const full = Math.max(0, window.innerHeight - TOP_PX - ROW_PX);
    return { half: full / 2, full };
  };
  const settle = () => {
    drag.current = null;
    if (sheet.current !== null) sheet.current.style.height = "";
  };
  const press = (event: PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startY: event.clientY, pointer: event.pointerId };
  };
  const move = (event: PointerEvent<HTMLButtonElement>) => {
    const at = drag.current;
    if (at === null || at.pointer !== event.pointerId || sheet.current === null) return;
    const { half, full } = room();
    const from = height === "half" ? half : full;
    // The sheet follows the finger between closed and full; it settles when let go.
    const followed = Math.min(full, Math.max(0, from - (event.clientY - at.startY)));
    sheet.current.style.height = `${followed}px`;
  };
  const release = (event: PointerEvent<HTMLButtonElement>) => {
    const at = drag.current;
    if (at === null || at.pointer !== event.pointerId) return;
    settle();
    const dy = event.clientY - at.startY;
    // A tap: its own click turns the height, so the click lands on the handle, not on whatever the
    // moved sheet would put under the finger.
    if (Math.abs(dy) < TAP_PX) return;
    // A mouse's drag ends in a click where it is let go, maybe on a clip or a tab, which is not
    // theirs: it is swallowed. A touch's drag makes no click, so the next press or key ends the wait.
    const endSwallow = () => {
      window.removeEventListener("click", swallow, true);
      window.removeEventListener("pointerdown", endSwallow, true);
      window.removeEventListener("keydown", endSwallow, true);
    };
    const swallow = (click: MouseEvent) => {
      click.stopPropagation();
      click.preventDefault();
      endSwallow();
    };
    window.addEventListener("click", swallow, true);
    window.addEventListener("pointerdown", endSwallow, true);
    window.addEventListener("keydown", endSwallow, true);
    const { half, full } = room();
    const stop = sheetStop(height, dy, half, full);
    if (stop === "closed") onClose();
    else onHeight(stop);
  };

  return (
    <div
      ref={sheet}
      className={classes.sheet}
      data-phone={phone || undefined}
      data-open={shown || undefined}
      data-height={shown ? height : undefined}
      role={shown ? "dialog" : undefined}
      aria-modal={shown ? false : undefined}
      aria-labelledby={shown ? titleId : undefined}
    >
      {props.children}
      {shown ? (
        <div className={classes.sheetChrome} data-sheet-chrome>
          <button
            type="button"
            className={classes.sheetGrab}
            aria-label={height === "half" ? `Show all of ${title}` : `Show half of ${title}`}
            onPointerDown={press}
            onPointerMove={move}
            onPointerUp={release}
            onPointerCancel={settle}
            onClick={() => onHeight(height === "half" ? "full" : "half")}
          >
            <span aria-hidden />
          </button>
          <div className={classes.sheetHead}>
            <Text className={classes.sheetTitle} id={titleId}>
              {title}
            </Text>
            <ActionIcon variant="subtle" color="gray" size={44} aria-label={`Close ${title}`} onClick={onClose}>
              <X size={18} />
            </ActionIcon>
          </div>
        </div>
      ) : null}
    </div>
  );
}
